import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import type { LoginResponse, AuthUser, MemberRole } from '@freelancer-os/shared-types';
import type { LoginInput, RegisterInput, OnboardingInput } from '@freelancer-os/validation';
import { fetchPublicApi, fetchApi } from '../lib/api';
import { useAuthStore } from '../stores/authStore';

interface AuthMeResponse {
  user: AuthUser;
  organization: { id: string; name: string; slug: string; logoUrl: string | null };
  role: MemberRole;
  isOnboarded: boolean;
}

/** Validate current session by calling /auth/me */
export function useAuthMe() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const setAuth = useAuthStore((s) => s.setAuth);
  const setRole = useAuthStore((s) => s.setRole);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const setLoading = useAuthStore((s) => s.setLoading);

  const query = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => fetchApi<AuthMeResponse>('/auth/me'),
    enabled: !!accessToken,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // Sync server state → auth store via effect (not during render)
  useEffect(() => {
    if (query.isSuccess && query.data) {
      const { refreshToken } = useAuthStore.getState();
      setAuth({
        user: query.data.user,
        organization: query.data.organization,
        tokens: { accessToken: accessToken!, refreshToken: refreshToken! },
        role: query.data.role,
      });
      setRole(query.data.role);
    }
    if (query.isError) {
      clearAuth();
    }
    if (query.isFetched) {
      setLoading(false);
    }
  }, [query.isSuccess, query.isError, query.isFetched, query.data, accessToken, setAuth, setRole, clearAuth, setLoading]);

  const isOnboarded = query.data?.isOnboarded ?? true; // default true until API responds

  return { ...query, isOnboarded };
}

export function useLogin() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (input: LoginInput) =>
      fetchPublicApi<LoginResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (data) => {
      setAuth({
        user: data.user,
        organization: data.organization,
        tokens: data.tokens,
      });
      navigate('/');
    },
  });
}

export function useRegister() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (input: RegisterInput) =>
      fetchPublicApi<LoginResponse>('/auth/register', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (data) => {
      setAuth({
        user: data.user,
        organization: data.organization,
        tokens: data.tokens,
      });
      navigate('/onboarding');
    },
  });
}

export function useLogout() {
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      fetchPublicApi<{ message: string }>('/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      }),
    onSettled: () => {
      clearAuth();
      queryClient.clear();
      navigate('/login');
    },
  });
}

export function useOnboarding() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: OnboardingInput) =>
      fetchApi<{ message: string }>('/auth/onboarding', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      navigate('/');
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) =>
      fetchPublicApi<{ message: string }>('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),
  });
}

export function useResetPassword() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (input: { token: string; password: string }) =>
      fetchPublicApi<{ message: string }>('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      navigate('/login');
    },
  });
}

export function useVerifyEmail() {
  return useMutation({
    mutationFn: (token: string) =>
      fetchPublicApi<{ message: string }>(`/auth/verify-email?token=${token}`, {
        method: 'GET',
      }),
  });
}
